import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { apiGet } from '../lib/api';

const AuthContext = createContext(null);
const sessionWithTimeout = () => Promise.race([
  supabase.auth.getSession(),
  new Promise((_, reject) => setTimeout(() => reject(new Error('Session lookup timed out')), 5000)),
]);

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshProfile = useCallback(async () => {
    try {
      const { data } = await apiGet('/auth/me');
      setProfile(data?.user?.profile || null);
      setBusiness(data?.business || null);
      return data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    sessionWithTimeout()
      .then(async ({ data }) => {
        if (!mounted) return;
        setSession(data.session);
        if (data.session) await refreshProfile();
      })
      .catch(() => {
        if (!mounted) return;
        setSession(null);
        setProfile(null);
        setBusiness(null);
      })
      .finally(() => {
        if (mounted) setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, s) => {
      setSession(s);
      try {
        if (s) await refreshProfile();
        else { setProfile(null); setBusiness(null); }
      } catch {
        if (!s) {
          setProfile(null);
          setBusiness(null);
        }
      }
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [refreshProfile]);

  const signIn = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signUp = async (email, password, meta = {}) => {
    const { error } = await supabase.auth.signUp({ email, password, options: { data: meta } });
    if (error) throw error;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setBusiness(null);
  };

  const role = profile?.role || null;

  return (
    <AuthContext.Provider value={{ session, user: session?.user || null, profile, business, role, loading, signIn, signUp, signOut, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
